import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Typography,
  Tooltip,
} from '@mui/material';
import { DescriptionOutlined } from '@mui/icons-material';
import { Skeletons } from '@prismamedia/one-components';
import { format as formatDate } from 'date-fns';
import fr from 'date-fns/locale/fr';
import { useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { CategoryFormat } from '../../../../../../../__generated__/queries-web';
import { useGlobalBrandKey } from '../../../../../../../utils/globalState';
import { CategoriesContext } from '../../../../../categories.context';
import { CREATED_AT, UPDATED_AT } from '../../../../../constants';
import { FormContext } from '../../../../../form.context';
import { isPageCreateView, isPageEditView } from '../../../../../utils';
import { GET_CATEGORY_ARTICLE_COUNTS } from '../../../../../apollo/queries/categories.web.graphql';
import type {
  GetCategoryArticleCountsQuery,
  GetCategoryArticleCountsQueryVariables,
} from '../../../../../../../__generated__/queries-web';
import { useStyles } from './styles';

export const Title = () => {
  const [brandKey] = useGlobalBrandKey();
  const { isCategoriesLoading, activeCategory } = useContext(CategoriesContext);
  const { form } = useContext(FormContext);
  const loading = isCategoriesLoading && isPageEditView();

  // Fetch article counts separately
  const { data: countsData, loading: isCountsLoading } = useQuery<
    GetCategoryArticleCountsQuery,
    GetCategoryArticleCountsQueryVariables
  >(GET_CATEGORY_ARTICLE_COUNTS, {
    variables: {
      where: {
        id: activeCategory?.id || '',
      },
      categoryId: activeCategory?.id || '',
    },
    skip:
      !activeCategory?.id ||
      !isPageEditView() ||
      !brandKey ||
      isPageCreateView(),
  });

  // Get the appropriate article count based on the category format
  const getArticleCount = useMemo(() => {
    // Don't show article count when creating a new category
    if (isPageCreateView()) return 0;

    const { format } = activeCategory || {};

    switch (format) {
      case CategoryFormat.Category:
        return countsData?.articleCategoryCount ?? 0;
      case CategoryFormat.Channel:
        return countsData?.articleChannelCount ?? 0;
      case CategoryFormat.Event:
        return countsData?.articleEventCount ?? 0;
      case CategoryFormat.Guide:
        return countsData?.articleGuideCount ?? 0;
      default:
        return countsData?.articleCategoryCount ?? 0;
    }
  }, [countsData, activeCategory]);

  const classes = useStyles({
    isLoading: loading,
  });

  const subTitle = useMemo(() => {
    // Don't show creation/update dates when creating a new category
    if (isPageCreateView()) return null;

    const createdDate = form?.createdAt
      ? `${CREATED_AT} ${formatDate(
          new Date(form.createdAt),
          'dd/MM/yyyy à HH:mm',
          { locale: fr },
        )}`
      : null;

    const updatedAt = form?.updatedAt
      ? `${UPDATED_AT} ${formatDate(
          new Date(form.updatedAt),
          'dd/MM/yyyy à HH:mm',
          { locale: fr },
        )}`
      : null;

    if (!updatedAt && !createdDate) {
      return null;
    }

    return `${updatedAt} ${createdDate ? `- ${createdDate}` : ''}.`;
  }, [form]);

  return (
    <span className={classes.wrapper}>
      <Grid container spacing={0.5} alignItems="center">
        <Grid item style={{ maxWidth: '70%' }}>
          {loading ? (
            <Skeletons width={200} height={14} />
          ) : (
            <Box className={classes.title} component="span">
              {form.title}
            </Box>
          )}
        </Grid>
        <Grid item xs={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              className={classes.brandKeyChip}
              label={brandKey}
              variant="filled"
              size="small"
            />
            <Tooltip title="Articles liés" arrow>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  height: '100%',
                  marginBottom: '2px',
                }}
              >
                <DescriptionOutlined
                  sx={{
                    color: 'white',
                    fontSize: '1.1rem',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                />
                {isCountsLoading ? (
                  <CircularProgress
                    size={12}
                    sx={{
                      color: 'white',
                      marginTop: '4px',
                    }}
                  />
                ) : (
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      lineHeight: 1,
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '4px',
                    }}
                  >
                    {getArticleCount}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          </Box>
        </Grid>

        <Grid container item>
          {loading ? (
            <Skeletons width={350} height={12} />
          ) : (
            subTitle && (
              <Typography className={classes.subTitle} variant="caption">
                {subTitle}
              </Typography>
            )
          )}
        </Grid>
      </Grid>
    </span>
  );
};
